import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import Sidebar from './components/Sidebar';

// ── Pages ──────────────────────────────────────────────
import HomePage from './pages/HomePage';
import MaintenanceListPage from './pages/MaintenanceListPage';
import MaintenanceFormPage from './pages/MaintenanceFormPage';
import EmployeesPage from './pages/EmployeesPage';
import SchoolsPage from './pages/SchoolsPage';
import UsersPage from './pages/UsersPage';
import PrintSinglePage from './pages/PrintSinglePage';
import PrintSchoolReportPage from './pages/PrintSchoolReportPage';
import PrintDetailPage from './pages/PrintDetailPage';
import PrintStatusReportPage from './pages/PrintStatusReportPage';
import PrintDetailReportPage from './pages/PrintDetailReportPage';
import ActivityLogPage from './pages/ActivityLogPage';

// ── Protected Route ────────────────────────────────────
function ProtectedRoute({ children, adminOnly = false, superAdminOnly = false }) {
  const { user, loading, isSuperAdmin, isAdminOrAbove } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400 text-sm">පූරණය වෙමින්...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (superAdminOnly && !isSuperAdmin)  return <Navigate to="/" />;
  if (adminOnly      && !isAdminOrAbove) return <Navigate to="/" />;

  return children;
}

// ── Layout with Navbar + Sidebar ───────────────────────
function Layout({ children }) {
  const isMobile = window.innerWidth < 768;
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex flex-1">
        {!isMobile && <Sidebar />}
        <main className="flex-1 p-4 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

// ── App Routes ─────────────────────────────────────────
function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400 text-sm">පූරණය වෙමින්...</div>
      </div>
    );
  }

  return (
    <Routes>

      {/* ── Public ── */}
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to="/" />} />

      {/* ── All logged in users ── */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><HomePage /></Layout>
        </ProtectedRoute>
      }/>

      <Route path="/maintenance" element={
        <ProtectedRoute>
          <Layout><MaintenanceListPage /></Layout>
        </ProtectedRoute>
      }/>

      {/* ── Admin and above ── */}
      <Route path="/maintenance/new" element={
        <ProtectedRoute adminOnly>
          <Layout><MaintenanceFormPage /></Layout>
        </ProtectedRoute>
      }/>

      <Route path="/maintenance/:id/edit" element={
        <ProtectedRoute adminOnly>
          <Layout><MaintenanceFormPage /></Layout>
        </ProtectedRoute>
      }/>

      <Route path="/employees" element={
        <ProtectedRoute adminOnly>
          <Layout><EmployeesPage /></Layout>
        </ProtectedRoute>
      }/>

      <Route path="/schools" element={
        <ProtectedRoute adminOnly>
          <Layout><SchoolsPage /></Layout>
        </ProtectedRoute>
      }/>

      {/* ── Super Admin only ── */}
      <Route path="/users" element={
        <ProtectedRoute superAdminOnly>
          <Layout><UsersPage /></Layout>
        </ProtectedRoute>
      }/>

      <Route path="/activity-log" element={
        <ProtectedRoute superAdminOnly>
          <Layout><ActivityLogPage /></Layout>
        </ProtectedRoute>
      }/>

      {/* ── Print routes (no navbar/sidebar) ── */}
      <Route path="/maintenance/:id/print" element={
        <ProtectedRoute>
          <PrintSinglePage />
        </ProtectedRoute>
      }/>

      <Route path="/maintenance/:id/print-detail" element={
        <ProtectedRoute>
          <PrintDetailPage />
        </ProtectedRoute>
      }/>

      <Route path="/print/school-report" element={
        <ProtectedRoute>
          <PrintSchoolReportPage />
        </ProtectedRoute>
      }/>

      <Route path="/print/status-report" element={
        <ProtectedRoute>
          <PrintStatusReportPage />
        </ProtectedRoute>
      }/>

      <Route path="/print/detail-report" element={
        <ProtectedRoute>
          <PrintDetailReportPage />
        </ProtectedRoute>
      }/>

      {/* ── Fallback ── */}
      <Route path="*" element={<Navigate to="/" />} />

    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}